import { convertToFormattedDms } from "@nova/calculate-geospatial"
import {
    GenericImage,
    GenericUpload,
    ImageProcessingState,
    ImageWarnings,
    isUsable,
    LoadingErrorMaybe,
} from "@nova/constants"
import { get } from "nodash"
import { isMalformedString } from "tools/strings/isNonEmptyString"

export enum UploadInformation {
    Created = "Created",
    Dimensions = "Dimensions",
    FieldOfView = "Field of View",
    Device = "Device",
    FocalLength = "Focal Length",
    Longitude = "Longitude",
    Latitude = "Latitude",
}

export const getSummaryFromUploads = (
    uploads?: (GenericImage | LoadingErrorMaybe<GenericUpload, unknown>)[]
) => {
    const summary = {
        stateCount: {} as Record<ImageProcessingState, number>,
        uploadCount: uploads?.length || 0,
        errorCount: 0,
        warnings: {} as ImageWarnings,
    }
    uploads?.forEach((upload) => {
        const usable = isUsable(upload)
        if (!usable) return summary.errorCount++
        summary.stateCount[usable.state] = ++summary.stateCount[usable.state] || 1
    })
    return summary
}

export const extractInformationFromUpload = (
    upload: LoadingErrorMaybe<GenericUpload>,
    infoToExtract: UploadInformation
) => {
    switch (infoToExtract) {
        case UploadInformation.Created:
            const takenAt = get(upload, "takenAt")
            if (!takenAt) return
            const date = new Date((takenAt as number) * 1000)

            const options: Intl.DateTimeFormatOptions = {
                month: "long",
                day: "numeric",
                year: "numeric",
                hour: "numeric",
                minute: "2-digit",
                hour12: true,
            }

            const formatted = date.toLocaleString("en-US", options)
            if (!formatted) return
            return formatted.replace(",", "")
        case UploadInformation.Dimensions:
            const imageHeight = get(upload, "imageHeight", 0)
            const imageWidth = get(upload, "imageWidth", 0)
            if (!imageHeight || !imageWidth) return
            return imageWidth + " x " + imageHeight
        case UploadInformation.FieldOfView:
            const fovArray = get(upload, "fov", [])
            if (!fovArray || fovArray.length === 0) return
            const part1 = fovArray[0]
            const part2 = fovArray[1]
            if (!part1 || !part2) return
            return `${parseInt(part1)}° x ${parseInt(part2)}°`
        case UploadInformation.Device:
            const make = get(upload, "make", "")
            const model = get(upload, "model", "")
            let device = ""
            if (!isMalformedString(make)) {
                device = `${make} `
            }
            if (!isMalformedString(model)) {
                device = `${device}${model}`
            }
            return device
        case UploadInformation.FocalLength:
            const focalLength = get(upload, "focalLength")
            if (!focalLength) return
            return parseInt(focalLength as string) + " mm"
        case UploadInformation.Longitude:
            const longitude = get(upload, "longitude", 0)
            if (!longitude) return
            return convertToFormattedDms(longitude, true)
        case UploadInformation.Latitude:
            const latitude = get(upload, "latitude", 0)
            if (!latitude) return
            return convertToFormattedDms(latitude, false)
        default:
            return ""
    }
}

export const fileUploadUsage: Record<string, number> = {}
export const flightExceededUsage: Record<string, number> = {}
